import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { STORAGE_KEYS } from '../constants/storageKeys';

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const DEFAULT_THEME: Theme = 'light';
const THEME_DOM_ATTRIBUTE = 'data-theme';
const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';
const EXPLICIT_FLAG = 'true';

/**
 * A `theme` entry only counts as a standing preference once
 * `STORAGE_KEYS.themeExplicit` is set — i.e. the visitor used the header
 * toggle on THIS browser. Without the flag, a `theme` value is either
 * unset or a value this module previously auto-derived from
 * `prefers-color-scheme` and wrote back as if it were a choice (the bug:
 * that pinned whichever theme the browser guessed on first load, so
 * changing the OS/browser dark-mode setting afterwards had no effect).
 * Treating it as non-authoritative here makes existing installs
 * self-healing — the very next load falls through to `readSystemTheme()`.
 */
const readExplicitTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null;
  if (window.localStorage.getItem(STORAGE_KEYS.themeExplicit) !== EXPLICIT_FLAG) return null;
  const stored = window.localStorage.getItem(STORAGE_KEYS.theme);
  return stored === 'light' || stored === 'dark' ? stored : null;
};

const readSystemTheme = (): Theme => {
  const prefersDark =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(PREFERS_DARK_QUERY).matches;
  return prefersDark ? 'dark' : DEFAULT_THEME;
};

const readInitialTheme = (): Theme => readExplicitTheme() ?? readSystemTheme();

const persistExplicitTheme = (theme: Theme): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEYS.theme, theme);
  window.localStorage.setItem(STORAGE_KEYS.themeExplicit, EXPLICIT_FLAG);
};

const applyThemeToDocument = (theme: Theme): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(THEME_DOM_ATTRIBUTE, theme);
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Named `themeState` so it pairs with `setThemeState` (sonarjs S6754 wants a
  // useState pair to share one name). The context still exposes it as `theme`;
  // `setTheme` below is the wrapper that also records the explicit choice.
  const [themeState, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    applyThemeToDocument(themeState);
  }, [themeState]);

  // Follow the OS/browser dark-mode setting live for as long as this
  // browser has no explicit in-app choice recorded, so fixing that setting
  // (as opposed to using the header toggle) takes effect without a reload.
  // `handleChange` re-checks `readExplicitTheme()` on every event rather
  // than only at subscribe time, so it stops overriding the moment the
  // visitor DOES make an explicit choice mid-session.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mediaQuery = window.matchMedia(PREFERS_DARK_QUERY);
    const handleChange = (event: MediaQueryListEvent): void => {
      if (readExplicitTheme() !== null) return;
      setThemeState(event.matches ? 'dark' : DEFAULT_THEME);
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((next: Theme): void => {
    persistExplicitTheme(next);
    setThemeState(next);
  }, []);

  // Functional updater (nexa-1115 review, 2026-09-17) — reading `themeState`
  // from the closure meant two toggles issued before a re-render (e.g. two
  // listeners wired to the same control) both computed the same `next` from
  // the same stale value, so the second call failed to invert the theme.
  const toggleTheme = useCallback((): void => {
    setThemeState((prev) => {
      const next: Theme = prev === 'light' ? 'dark' : 'light';
      persistExplicitTheme(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme: themeState, setTheme, toggleTheme }),
    [themeState, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
};
